import { Label, Switch } from "@nymbl/nos2-rules";
// src/components/pto/ApprovalsPanel.tsx
import { useState } from "react";
import { type PtoEmailFocus, PtoRequestList } from "@/components/team/PtoRequestList";
import { useApprovals, useApprovePtoAny, useBookPtoAny, useRejectPtoAny } from "@/hooks/use-pto";
import { usePtoRequestList } from "@/hooks/use-pto-request-list";
import type { PtoApprovalScope, PtoTab } from "@/lib/enums";
import { defaultTab } from "@/lib/team/pto-list";

/**
 * PTO -> Approvals (spec 2): managers, stored approvers and HR decide every request they may
 * decide, through the shared My Team list with scope "any". HR alone gets "Everyone" (scope
 * "all", enforced again on the server).
 */
export function ApprovalsPanel({
  isHrApprover,
  focus,
  onFocusDone,
}: {
  isHrApprover: boolean;
  /** From an approver's email: open this request's approve or reject step once it has loaded. */
  focus?: PtoEmailFocus | null;
  onFocusDone?: () => void;
}) {
  const [scope, setScope] = useState<PtoApprovalScope>("mine");
  const [tab, setTab] = useState<PtoTab | null>(null);
  const query = useApprovals(scope, true);
  const approve = useApprovePtoAny();
  const book = useBookPtoAny();
  const reject = useRejectPtoAny();
  const state = usePtoRequestList(query.data?.rows, query.dataUpdatedAt);
  const shownTab = tab ?? (query.data ? defaultTab(query.data.rows) : "pending");

  return (
    <section aria-label="Approvals" className="space-y-3">
      <PtoRequestList
        view={query.data}
        viewUpdatedAt={query.dataUpdatedAt}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        tab={shownTab}
        onTabChange={setTab}
        focus={focus}
        onFocusDone={() => {
          setTab("pending");
          onFocusDone?.();
        }}
        state={state}
        calls={{
          approve: (id) => approve.mutateAsync({ requestId: id }),
          book: (id) => book.mutateAsync({ requestId: id }),
          reject: (id, note) => reject.mutateAsync({ requestId: id, note }),
        }}
        toolbar={
          isHrApprover ? (
            <div className="flex min-h-11 items-center gap-2">
              <Switch
                id="approvals-everyone"
                checked={scope === "all"}
                onCheckedChange={(on) => {
                  setScope(on ? "all" : "mine");
                  setTab(null);
                }}
              />
              <Label
                htmlFor="approvals-everyone"
                className="min-h-11 cursor-pointer whitespace-nowrap"
              >
                Everyone
              </Label>
            </div>
          ) : undefined
        }
      />
    </section>
  );
}
